import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Check, Plus, Trash2, Sun, Moon, Clock, Sparkles, Save, Info, FileText, Loader2, X } from 'lucide-react';
import Logo from '../components/common/Logo';
import MoonIcon from '../components/common/MoonIcon';
import ProgressSteps from '../components/common/ProgressSteps';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { howBabySleepsOptions, whereBabySleepsOptions } from '../data/mockData';
import apiService, { convertReportToFrontend } from '../services/api';

// Componente Select compatível com iOS - SEMPRE usa select nativo para evitar bugs do Radix UI
const MobileSelect = ({ value, onValueChange, options, placeholder = "Selecione...", className = "" }) => {
  // SEMPRE usar select nativo para máxima compatibilidade com iOS/Safari
  return (
    <select
      value={value || ''}
      onChange={(e) => onValueChange(e.target.value)}
      className={`flex h-10 w-full items-center justify-between rounded-md border border-[#FAE0D5] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#8F7AC5] focus:border-transparent appearance-none bg-[url('data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22292.4%22%20height%3D%22292.4%22%3E%3Cpath%20fill%3D%22%238F7AC5%22%20d%3D%22M287%2069.4a17.6%2017.6%200%200%200-13-5.4H18.4c-5%200-9.3%201.8-12.9%205.4A17.6%2017.6%200%200%200%200%2082.2c0%205%201.8%209.3%205.4%2012.9l128%20127.9c3.6%203.6%207.8%205.4%2012.8%205.4s9.2-1.8%2012.8-5.4L287%2095c3.5-3.5%205.4-7.8%205.4-12.8%200-5-1.9-9.2-5.5-12.8z%22%2F%3E%3C%2Fsvg%3E')] bg-[length:0.65em] bg-[right_0.7rem_center] bg-no-repeat ${className}`}
    >
      <option value="" disabled>{placeholder}</option>
      {options.map(opt => (
        <option key={opt} value={opt}>{opt}</option>
      ))}
    </select>
  );
};

// Opções de horário simplificadas
const hourOptions = Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0'));
const minuteOptions = ['00', '10', '20', '30', '40', '50'];

// Componente de seleção de horário com botão "Agora"
const TimeSelector = ({ value, onChange, showNowButton = true }) => {
  const [hour, minute] = (value || '').split(':');
  
  const handleHourChange = (newHour) => {
    onChange(`${newHour}:${minute || '00'}`);
  };
  
  const handleMinuteChange = (newMinute) => {
    onChange(`${hour || '06'}:${newMinute}`);
  };

  const handleNow = () => {
    const now = new Date();
    const currentHour = now.getHours().toString().padStart(2, '0');
    const currentMinute = now.getMinutes().toString().padStart(2, '0');
    onChange(`${currentHour}:${currentMinute}`);
  };

  // Verificar se o minuto atual está nas opções ou é personalizado
  const isCustomMinute = minute && !minuteOptions.includes(minute);

  return (
    <div className="flex items-center gap-1">
      <select
        value={hour || ''}
        onChange={(e) => handleHourChange(e.target.value)}
        className="h-9 px-1 text-sm border border-[#8F7AC5]/30 rounded-md bg-white focus:outline-none focus:border-[#8F7AC5] appearance-none text-center w-12"
      >
        <option value="">--</option>
        {hourOptions.map(h => (
          <option key={h} value={h}>{h}</option>
        ))}
      </select>
      <span className="text-[#6B5B8C] font-bold">:</span>
      <select
        value={minute || ''}
        onChange={(e) => handleMinuteChange(e.target.value)}
        className="h-9 px-1 text-sm border border-[#8F7AC5]/30 rounded-md bg-white focus:outline-none focus:border-[#8F7AC5] appearance-none text-center w-12"
      >
        <option value="">--</option>
        {isCustomMinute && <option value={minute}>{minute}</option>}
        {minuteOptions.map(m => (
          <option key={m} value={m}>{m}</option>
        ))}
      </select>
      {showNowButton && (
        <button
          type="button"
          onClick={handleNow}
          className="h-9 px-2 text-xs font-medium bg-[#D98DAE] text-white rounded-md hover:bg-[#C77A9D] active:scale-95 transition-all"
        >
          Agora
        </button>
      )}
    </div>
  );
};

// Opções de atividades para a rotina (formato simplificado para autocomplete)
const allRoutineActivities = [
  // Alimentação
  'Mamou no peito',
  'Mamou mamadeira',
  'Café da manhã',
  'Lanche da manhã',
  'Almoço',
  'Lanche da tarde',
  'Jantar',
  'Fruta',
  'Papinha',
  'Água',
  // Sono
  'Acordou',
  'Dormiu para soneca',
  'Acordou da soneca',
  'Dormiu para noite',
  'Despertou à noite',
  // Sinais de sono
  'Bocejou',
  'Coçou os olhos',
  'Ficou irritado',
  'Olhar vidrado',
  'Puxou a orelha',
  'Chorou de sono',
  // Atividades
  'Brincou no tapete',
  'Brincou livre',
  'Banho',
  'Troca de fralda',
  'Passeio',
  'Tempo na cadeirinha',
  'Tempo no colo',
  'Tummy time',
  // Outros comuns
  'Chorou',
  'Regurgitou',
  'Fez cocô',
  'Vacina',
  'Medicamento'
];

// Componente de Autocomplete para atividades
const ActivityAutocomplete = ({ value, onChange, placeholder }) => {
  const [inputValue, setInputValue] = useState(value || '');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredSuggestions, setFilteredSuggestions] = useState([]);
  const [isSelectingSuggestion, setIsSelectingSuggestion] = useState(false);
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);

  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  useEffect(() => {
    // Fechar sugestões ao clicar fora
    const handleClickOutside = (e) => {
      if (inputRef.current && !inputRef.current.contains(e.target) &&
          suggestionsRef.current && !suggestionsRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);

  const handleInputChange = (e) => {
    const val = e.target.value;
    setInputValue(val);
    
    if (val.trim().length > 0) {
      const filtered = allRoutineActivities.filter(activity =>
        activity.toLowerCase().includes(val.toLowerCase())
      );
      setFilteredSuggestions(filtered);
      setShowSuggestions(true);
    } else {
      setFilteredSuggestions(allRoutineActivities.slice(0, 8)); // Mostrar as primeiras 8
      setShowSuggestions(true);
    }
  };

  const handleFocus = () => {
    if (inputValue.trim().length > 0) {
      const filtered = allRoutineActivities.filter(activity =>
        activity.toLowerCase().includes(inputValue.toLowerCase())
      );
      setFilteredSuggestions(filtered);
    } else {
      setFilteredSuggestions(allRoutineActivities.slice(0, 8));
    }
    setShowSuggestions(true);
  };

  const handleSelectSuggestion = (suggestion) => {
    setIsSelectingSuggestion(true);
    setInputValue(suggestion);
    onChange(suggestion);
    setShowSuggestions(false);
    // Reset flag após um tempo
    setTimeout(() => setIsSelectingSuggestion(false), 300);
  };

  const handleBlur = () => {
    // Se estiver selecionando uma sugestão, não fazer nada
    if (isSelectingSuggestion) return;
    
    // Delay maior para mobile
    setTimeout(() => {
      if (!isSelectingSuggestion && inputValue.trim()) {
        onChange(inputValue.trim());
      }
      setShowSuggestions(false);
    }, 300);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (inputValue.trim()) {
        onChange(inputValue.trim());
        setShowSuggestions(false);
      }
    }
  };

  return (
    <div className="relative flex-1">
      <input
        ref={inputRef}
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        placeholder={placeholder || "Digite a atividade..."}
        className="w-full h-9 px-3 text-sm border border-[#8F7AC5]/30 rounded-md bg-white focus:outline-none focus:border-[#8F7AC5] focus:ring-1 focus:ring-[#8F7AC5]"
      />
      {showSuggestions && filteredSuggestions.length > 0 && (
        <div 
          ref={suggestionsRef}
          className="absolute z-50 w-full mt-1 bg-white border border-[#FAE0D5] rounded-lg shadow-lg max-h-48 overflow-y-auto"
        >
          {filteredSuggestions.map((suggestion, index) => (
            <button
              key={index}
              type="button"
              onMouseDown={(e) => {
                e.preventDefault(); // Previne blur antes do click
                handleSelectSuggestion(suggestion);
              }}
              onTouchStart={(e) => {
                e.preventDefault(); // Previne blur antes do touch
                handleSelectSuggestion(suggestion);
              }}
              className="w-full px-3 py-2 text-left text-sm hover:bg-[#FBF4FF] active:bg-[#E8D4F0] focus:bg-[#FBF4FF] focus:outline-none border-b border-[#FAE0D5]/50 last:border-b-0"
            >
              {suggestion}
            </button>
          ))}
        </div>
      )}
      {showSuggestions && inputValue.trim() && filteredSuggestions.length === 0 && (
        <div 
          ref={suggestionsRef}
          className="absolute z-50 w-full mt-1 bg-white border border-[#FAE0D5] rounded-lg shadow-lg p-3"
        >
          <p className="text-xs text-[#6B5B8C]">
            Pressione Enter para adicionar: <strong>"{inputValue}"</strong>
          </p>
        </div>
      )}
    </div>
  );
};

// Opções de atividades para a rotina (mantido para compatibilidade)
const routineActivityOptions = [
  { category: 'Alimentação', items: ['Mamou no peito', 'Mamou mamadeira', 'Café da manhã', 'Lanche da manhã', 'Almoço', 'Lanche da tarde', 'Jantar', 'Fruta'] },
  { category: 'Sono', items: ['Acordou', 'Dormiu para soneca', 'Acordou da soneca', 'Dormiu para noite', 'Despertou à noite'] },
  { category: 'Sinais de sono', items: ['Bocejou', 'Coçou os olhos', 'Ficou irritado', 'Olhar vidrado', 'Puxou a orelha'] },
  { category: 'Atividades', items: ['Brincou no tapete', 'Brincou livre', 'Banho', 'Troca de fralda', 'Passeio', 'Tempo na cadeirinha', 'Tempo no colo'] },
  { category: 'Outros', items: ['Outro (digitar)'] }
];

// Opções para despertares noturnos
const nightWakingsOptions = [
  '0 - Dormiu a noite toda',
  '1 vez',
  '2 vezes',
  '3 vezes',
  '4 vezes',
  '5 ou mais vezes',
  'Outro (digitar)'
];

// Opções para duração dos despertares
const nightWakingsDurationOptions = [
  'Menos de 5 minutos',
  '5-10 minutos',
  '10-20 minutos',
  '20-30 minutos',
  '30-60 minutos',
  'Mais de 1 hora',
  'Variou muito',
  'Outro (digitar)'
];

// Opções para duração das sonecas
const napDurationOptions = [
  '15 minutos ou menos',
  '20 minutos',
  '30 minutos',
  '45 minutos',
  '1 hora',
  '1h30',
  '2 horas',
  '2h30 ou mais',
  'Outro (digitar)'
];

// Opções para tempo para dormir
const timeToSleepOptions = [
  'Menos de 5 minutos',
  '5-10 minutos',
  '10-15 minutos',
  '15-20 minutos',
  '20-30 minutos',
  '30-45 minutos',
  '45-60 minutos',
  'Mais de 1 hora',
  'Outro (digitar)'
];

const RoutineRegistrationPage = () => {
  const { reportId } = useParams();
  const navigate = useNavigate();
  
  // Helper seguro para localStorage (iOS Safari modo privado pode falhar)
  const safeLocalStorage = {
    getItem: (key) => {
      try {
        return localStorage.getItem(key);
      } catch (e) {
        console.warn('localStorage not available:', e);
        return null;
      }
    },
    setItem: (key, value) => {
      try {
        localStorage.setItem(key, value);
        return true;
      } catch (e) {
        console.warn('localStorage not available:', e);
        return false;
      }
    }
  };
  
  // Restaurar o último dia acessado do localStorage
  const getInitialDay = () => {
    try {
      const savedDay = safeLocalStorage.getItem(`routine_${reportId}_currentDay`);
      if (savedDay) {
        const day = parseInt(savedDay, 10);
        if (day >= 1 && day <= 5) return day;
      }
    } catch (e) {
      console.warn('Erro ao restaurar dia:', e);
    }
    return 1;
  };
  
  const [currentDay, setCurrentDay] = useState(getInitialDay);
  const [lastSaved, setLastSaved] = useState(null);
  const [showSaveNotice, setShowSaveNotice] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showInitialForm, setShowInitialForm] = useState(false);
  const [initialFormData, setInitialFormData] = useState(null);
  const [reportBasicInfo, setReportBasicInfo] = useState(null);
  const [loadingReport, setLoadingReport] = useState(true);
  const [pageError, setPageError] = useState(null);
  const pageTopRef = useRef(null);
  const totalDays = 5;
  
  // Salvar o dia atual sempre que mudar
  useEffect(() => {
    safeLocalStorage.setItem(`routine_${reportId}_currentDay`, currentDay.toString());
  }, [currentDay, reportId]);

  const createEmptyRoutineData = () => {
    return Array.from({ length: 5 }, (_, i) => ({
      day: i + 1,
      date: '',
      wakeUpTime: '',
      bedTime: '',
      nightWakings: '',
      nightWakingsDuration: '',
      noNaps: false,
      naps: [{ startTime: '', duration: '', timeToSleep: '', howSlept: '', whereSlept: '' }],
      routineEntries: [],
      routineDescription: '',
      importantNotes: '',
      sleepSignals: []
    }));
  };

  // Carregar dados salvos do localStorage - com tratamento de erro robusto
  const getInitialData = () => {
    try {
      const saved = safeLocalStorage.getItem(`routine_${reportId}`);
      if (saved) {
        const parsed = JSON.parse(saved);
        return parsed.routineData || createEmptyRoutineData();
      }
    } catch (e) {
      console.warn('Erro ao carregar dados iniciais:', e);
    }
    return createEmptyRoutineData();
  };

  const [routineData, setRoutineData] = useState(() => {
    try {
      return getInitialData();
    } catch (e) {
      console.error('Erro na inicialização de routineData:', e);
      return createEmptyRoutineData();
    }
  });

  // Carregar dados do backend ao iniciar
  useEffect(() => {
    const loadReportFromBackend = async () => {
      try {
        setLoadingReport(true);
        setPageError(null);
        const report = await apiService.getReport(reportId);
        const converted = convertReportToFrontend(report);
        
        if (converted) {
          // Salvar dados básicos e formulário inicial para exibição
          setReportBasicInfo({
            babyName: converted.babyName,
            babyAge: converted.babyAge,
            motherName: converted.motherName,
            fatherName: converted.fatherName,
            email: converted.email,
            whatsapp: converted.whatsapp,
          });
          setInitialFormData(converted.initialForm);
          
          // Verificar se há dados mais recentes no localStorage
          const localSaved = safeLocalStorage.getItem(`routine_${reportId}`);
          let localData = null;
          let localTimestamp = 0;
          
          if (localSaved) {
            try {
              const parsed = JSON.parse(localSaved);
              localData = parsed.routineData;
              localTimestamp = parsed.lastSaved ? new Date(parsed.lastSaved).getTime() : 0;
            } catch {
              // ignore
            }
          }
          
          // Se já houver rotinas no backend, mesclar com dados locais
          if (converted.routineDays && converted.routineDays.length > 0) {
            setRoutineData(prev => {
              // Começar com dados locais se existirem, senão usar prev
              const newData = localData ? [...localData] : [...prev];
              
              converted.routineDays.forEach(day => {
                if (day.day >= 1 && day.day <= 5) {
                  const dayIndex = day.day - 1;
                  const localDayData = newData[dayIndex] || {};
                  
                  // Helper para escolher valor: preferir dado que tem valor
                  // Se local tem valor, usar local; senão usar backend; senão default
                  const pickValue = (local, backend, defaultVal = '') => {
                    // Se local tem valor real (não vazio), usar local
                    if (local !== undefined && local !== null && local !== '') return local;
                    // Senão usar backend se tiver valor
                    if (backend !== undefined && backend !== null && backend !== '') return backend;
                    // Senão usar default
                    return defaultVal;
                  };
                  
                  // Para cada campo, preferir dado que existe
                  newData[dayIndex] = {
                    ...newData[dayIndex],
                    day: day.day,
                    date: pickValue(localDayData.date, day.date, ''),
                    wakeUpTime: pickValue(localDayData.wakeUpTime, day.wakeUpTime, ''),
                    bedTime: pickValue(localDayData.bedTime, day.bedTime, ''),
                    nightWakings: pickValue(localDayData.nightWakings, day.nightWakings, ''),
                    nightWakingsDuration: pickValue(localDayData.nightWakingsDuration, day.nightWakingsDuration, ''),
                    // Para arrays, preferir o que tiver dados
                    naps: (localDayData.naps && localDayData.naps.length > 0 && localDayData.naps.some(n => n.startTime || n.duration))
                      ? localDayData.naps 
                      : (day.naps && day.naps.length > 0 ? day.naps : [{ startTime: '', duration: '', timeToSleep: '', howSlept: '', whereSlept: '' }]),
                    routineEntries: (localDayData.routineEntries && localDayData.routineEntries.length > 0)
                      ? localDayData.routineEntries 
                      : (day.routineEntries || []),
                    routineDescription: pickValue(localDayData.routineDescription, day.routineDescription, ''),
                    importantNotes: pickValue(localDayData.importantNotes, day.importantNotes, ''),
                    noNaps: localDayData.noNaps !== undefined ? localDayData.noNaps : false,
                  };
                }
              });
              return newData;
            });
          } else if (localData) {
            // Se não há dados no backend mas há no localStorage, usar localStorage
            setRoutineData(localData);
          }
        }
      } catch (error) {
        console.log('Relatório não encontrado no backend:', error.message);
        // Não setar erro aqui, pois pode ser um relatório novo
      } finally {
        setLoadingReport(false);
      }
    };

    loadReportFromBackend();
  }, [reportId]);

  // Carregar dia salvo do localStorage
  useEffect(() => {
    const saved = safeLocalStorage.getItem(`routine_${reportId}`);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed.currentDay) {
          setCurrentDay(parsed.currentDay);
        }
      } catch {
        // ignore
      }
    }
  }, [reportId]);

  // Salvar automaticamente quando dados mudam (localStorage)
  useEffect(() => {
    const saveData = () => {
      const dataToSave = {
        routineData,
        currentDay,
        lastSaved: new Date().toISOString()
      };
      safeLocalStorage.setItem(`routine_${reportId}`, JSON.stringify(dataToSave));
      setLastSaved(new Date());
    };

    const debounceTimer = setTimeout(saveData, 500);
    return () => clearTimeout(debounceTimer);
  }, [routineData, currentDay, reportId]);

  // Auto-save para backend quando dados mudam (debounce de 2 segundos)
  const [autoSaveStatus, setAutoSaveStatus] = useState(''); // 'saving', 'saved', 'error'
  const autoSaveTimerRef = useRef(null);
  
  useEffect(() => {
    // Limpar timer anterior
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }
    
    // Criar novo timer para salvar após 2 segundos de inatividade
    autoSaveTimerRef.current = setTimeout(async () => {
      const dayData = routineData[currentDay - 1];
      // Verificar se há qualquer dado preenchido (incluindo sonecas completas)
      const hasNapData = dayData.naps && dayData.naps.some(n => 
        n.startTime || n.duration || n.timeToSleep || n.howSlept || n.whereSlept
      );
      const hasRoutineData = dayData.routineEntries && dayData.routineEntries.length > 0;
      const hasBasicData = dayData.wakeUpTime || dayData.bedTime || dayData.routineDescription || 
                           dayData.nightWakings || dayData.nightWakingsDuration || dayData.importantNotes;
      
      const hasData = hasBasicData || hasRoutineData || hasNapData;
      
      if (hasData && reportId) {
        setAutoSaveStatus('saving');
        try {
          await apiService.updateRoutineDay(reportId, currentDay, dayData);
          setAutoSaveStatus('saved');
          // Limpar status após 2 segundos
          setTimeout(() => setAutoSaveStatus(''), 2000);
        } catch (error) {
          console.error('Erro no auto-save:', error);
          setAutoSaveStatus('error');
        }
      }
    }, 2000);
    
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, [routineData, currentDay, reportId]);

  // Função para salvar dia atual no backend com timeout
  const saveDayToBackend = async (dayNumber, options = {}) => {
    const { showLoading = true, timeout = 10000 } = options;
    
    if (showLoading) setSaving(true);
    
    try {
      const dayData = routineData[dayNumber - 1];
      
      // Criar promise com timeout para evitar tela travada
      const savePromise = apiService.updateRoutineDay(reportId, dayNumber, dayData);
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Timeout')), timeout)
      );
      
      await Promise.race([savePromise, timeoutPromise]);
      return true;
    } catch (error) {
      console.error('Erro ao salvar no backend:', error);
      // Não mostrar erro para o usuário - dados estão salvos no localStorage
      return false;
    } finally {
      if (showLoading) setSaving(false);
    }
  };

  const currentDayData = routineData[currentDay - 1] || {
    day: currentDay,
    date: '',
    wakeUpTime: '',
    bedTime: '',
    nightWakings: '',
    nightWakingsDuration: '',
    noNaps: false,
    naps: [{ startTime: '', duration: '', timeToSleep: '', howSlept: '', whereSlept: '' }],
    routineEntries: [],
    routineDescription: '',
    importantNotes: '',
    sleepSignals: []
  };
  
  // Garantir que naps é sempre um array válido
  if (!currentDayData.naps || !Array.isArray(currentDayData.naps)) {
    currentDayData.naps = [{ startTime: '', duration: '', timeToSleep: '', howSlept: '', whereSlept: '' }];
  }
  
  // Garantir que routineEntries é sempre um array válido
  if (!currentDayData.routineEntries || !Array.isArray(currentDayData.routineEntries)) {
    currentDayData.routineEntries = [];
  }

  const updateDayField = (field, value) => {
    setRoutineData(prev => {
      const newData = [...prev];
      newData[currentDay - 1] = { ...newData[currentDay - 1], [field]: value };
      return newData;
    });
  };

  const updateNap = (napIndex, field, value) => {
    setRoutineData(prev => {
      const newData = [...prev];
      // Garantir que naps existe e é um array
      if (!newData[currentDay - 1].naps || !Array.isArray(newData[currentDay - 1].naps)) {
        newData[currentDay - 1].naps = [{ startTime: '', duration: '', timeToSleep: '', howSlept: '', whereSlept: '' }];
      }
      const naps = [...newData[currentDay - 1].naps];
      if (naps[napIndex]) {
        naps[napIndex] = { ...naps[napIndex], [field]: value };
        newData[currentDay - 1] = { ...newData[currentDay - 1], naps };
      }
      return newData;
    });
  };

  const addNap = () => {
    setRoutineData(prev => {
      const newData = [...prev];
      // Garantir que naps existe e é um array
      if (!newData[currentDay - 1].naps || !Array.isArray(newData[currentDay - 1].naps)) {
        newData[currentDay - 1].naps = [];
      }
      newData[currentDay - 1].naps.push({ startTime: '', duration: '', timeToSleep: '', howSlept: '', whereSlept: '' });
      return newData;
    });
  };

  const removeNap = (napIndex) => {
    setRoutineData(prev => {
      const newData = [...prev];
      // Garantir que naps existe e é um array
      if (newData[currentDay - 1].naps && Array.isArray(newData[currentDay - 1].naps)) {
        newData[currentDay - 1].naps = newData[currentDay - 1].naps.filter((_, i) => i !== napIndex);
      }
      return newData;
    });
  };

  // Funções para entradas de rotina estruturadas
  const addRoutineEntry = () => {
    setRoutineData(prev => {
      const newData = [...prev];
      const entries = newData[currentDay - 1].routineEntries || [];
      entries.push({ time: '', activity: '', customActivity: '' });
      newData[currentDay - 1] = { ...newData[currentDay - 1], routineEntries: entries };
      return newData;
    });
  };

  const updateRoutineEntry = (entryIndex, field, value) => {
    setRoutineData(prev => {
      const newData = [...prev];
      const entries = [...(newData[currentDay - 1].routineEntries || [])];
      entries[entryIndex] = { ...entries[entryIndex], [field]: value };
      newData[currentDay - 1] = { ...newData[currentDay - 1], routineEntries: entries };
      return newData;
    });
  };

  const removeRoutineEntry = (entryIndex) => {
    setRoutineData(prev => {
      const newData = [...prev];
      newData[currentDay - 1].routineEntries = (newData[currentDay - 1].routineEntries || []).filter((_, i) => i !== entryIndex);
      return newData;
    });
  };

  // Scroll para o topo ao mudar de dia
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const nextDay = async () => {
    // Validar campos obrigatórios
    const dayData = routineData[currentDay - 1];
    if (!dayData.wakeUpTime || !dayData.bedTime) {
      alert('Por favor, preencha os horários obrigatórios: "Acordou de manhã às" e "Horário que dormiu na noite anterior"');
      return;
    }
    
    // Salvar no localStorage imediatamente antes de mudar de dia
    const dataToSave = {
      routineData,
      currentDay,
      lastSaved: new Date().toISOString()
    };
    safeLocalStorage.setItem(`routine_${reportId}`, JSON.stringify(dataToSave));
    
    // Salvar dia atual no backend (com timeout de 10s para não travar a tela)
    // IMPORTANTE: Não bloquear navegação se falhar - dados estão seguros no localStorage
    const saveSuccess = await saveDayToBackend(currentDay, { timeout: 10000 });
    
    // Se falhou, tentar novamente em background depois
    if (!saveSuccess) {
      console.warn('Salvamento falhou, tentando novamente em background...');
      // Retry em background após 2 segundos
      setTimeout(() => {
        saveDayToBackend(currentDay, { showLoading: false, timeout: 30000 });
      }, 2000);
    }
    
    // Sempre avançar para o próximo dia (dados estão no localStorage)
    if (currentDay < totalDays) {
      setCurrentDay(prev => prev + 1);
      scrollToTop();
    } else {
      navigate(`/report/${reportId}/complete`);
    }
  };

  const prevDay = async () => {
    // Salvar no localStorage imediatamente antes de mudar de dia
    const dataToSave = {
      routineData,
      currentDay,
      lastSaved: new Date().toISOString()
    };
    safeLocalStorage.setItem(`routine_${reportId}`, JSON.stringify(dataToSave));
    
    // Salvar dia atual antes de voltar (com timeout de 10s)
    const saveSuccess = await saveDayToBackend(currentDay, { timeout: 10000 });
    
    // Se falhou, tentar novamente em background
    if (!saveSuccess) {
      setTimeout(() => {
        saveDayToBackend(currentDay, { showLoading: false, timeout: 30000 });
      }, 2000);
    }
    
    if (currentDay > 1) {
      setCurrentDay(prev => prev - 1);
      scrollToTop();
    } else {
      // Se estiver no dia 1, mostrar opção de ver informações iniciais
      setShowInitialForm(true);
    }
  };

  // Voltar para o formulário inicial (editar)
  const goToInitialForm = () => {
    navigate(`/report/${reportId}`);
  };

  const dayLabels = ['Dia 1', 'Dia 2', 'Dia 3', 'Dia 4', 'Dia 5'];

  // Mostrar tela de loading enquanto carrega dados do backend
  if (loadingReport) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#FBF4FF] to-white flex flex-col items-center justify-center">
        <Logo variant="v3SonoRotina" size="md" />
        <div className="mt-8 flex flex-col items-center">
          <Loader2 className="w-8 h-8 text-[#8F7AC5] animate-spin" />
          <p className="mt-4 text-[#6B5B8C] text-sm">Carregando registro de rotina...</p>
        </div>
      </div>
    );
  }

  // Mostrar erro se houver problema
  if (pageError) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#FBF4FF] to-white flex flex-col items-center justify-center p-4">
        <Logo variant="v3SonoRotina" size="md" />
        <div className="mt-8 bg-white rounded-2xl p-6 shadow-lg max-w-md text-center">
          <p className="text-red-500 mb-4">Ocorreu um erro ao carregar a página.</p>
          <p className="text-sm text-gray-600 mb-4">{pageError}</p>
          <button 
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-[#8F7AC5] text-white rounded-full"
          >
            Recarregar
          </button>
        </div>
      </div>
    );
  }

  // Verificar se currentDayData existe antes de renderizar
  if (!currentDayData) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#FBF4FF] to-white flex flex-col items-center justify-center">
        <Logo variant="v3SonoRotina" size="md" />
        <div className="mt-8 flex flex-col items-center">
          <Loader2 className="w-8 h-8 text-[#8F7AC5] animate-spin" />
          <p className="mt-4 text-[#6B5B8C] text-sm">Preparando formulário...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FBF4FF] to-white pb-32" ref={pageTopRef}>
      {/* Header */}
      <header className="py-4 px-4 border-b border-[#FAE0D5] bg-white sticky top-0 z-20">
        <div className="max-w-2xl mx-auto flex justify-between items-center">
          <Logo variant="v3SonoRotina" size="sm" />
          <div className="flex items-center gap-2">
            {lastSaved && (
              <span className="text-xs text-[#7BC47F] flex items-center gap-1">
                <Save className="w-3 h-3" />
                Salvo
              </span>
            )}
            <span className="text-sm text-[#8F7AC5] font-medium">Registro de Rotina</span>
          </div>
        </div>
      </header>

      {/* Aviso de salvamento automático */}
      <>
        {showSaveNotice && (
          <div
            
            
            
            className="max-w-2xl mx-auto px-4 py-3"
          >
            <div className="bg-[#8F7AC5]/10 border border-[#8F7AC5]/20 rounded-2xl p-4 flex items-start gap-3">
              <Info className="w-5 h-5 text-[#8F7AC5] flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-[#30244F] font-medium">Suas informações são salvas automaticamente! 💜</p>
                <p className="text-xs text-[#6B5B8C] mt-1">
                  Pode fechar a página tranquila. Quando voltar, continuará de onde parou.
                </p>
              </div>
              <button 
                onClick={() => setShowSaveNotice(false)}
                className="text-[#9890A8] hover:text-[#30244F] text-sm"
              >
                ✕
              </button>
            </div>
          </div>
        )}
      </>

      {/* Progress */}
      <div className="max-w-2xl mx-auto px-4 py-6">
        <ProgressSteps 
          currentStep={currentDay} 
          totalSteps={totalDays} 
          labels={dayLabels}
          completedSteps={routineData
            .map((day, index) => {
              // Considerar dia preenchido se tem horário de acordar E horário de dormir
              const hasRequiredFields = day.wakeUpTime && day.bedTime;
              return hasRequiredFields ? index + 1 : null;
            })
            .filter(Boolean)
          }
        />
      </div>

      {/* Day Header */}
      <div className="max-w-2xl mx-auto px-4 mb-6">
        <div 
          className="bg-gradient-to-r from-[#8F7AC5] to-[#D98DAE] rounded-3xl p-6 text-white text-center"
          
          
          key={currentDay}
        >
          <div className="flex items-center justify-center gap-3 mb-2">
            <MoonIcon variant="white" size="sm" />
            <h1 className="text-2xl font-bold">Dia {currentDay} de 5</h1>
            {/* Indicador de dia preenchido */}
            {currentDayData.wakeUpTime && currentDayData.bedTime && (
              <span className="bg-white/20 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
                <Check className="w-3 h-3" />
                Preenchido
              </span>
            )}
          </div>
          <p className="text-white/80 text-sm">
            {currentDayData.wakeUpTime && currentDayData.bedTime 
              ? 'Você pode revisar ou editar os dados deste dia.'
              : 'Registre tudo que aconteceu hoje. Quanto mais detalhes, melhor o plano!'
            }
          </p>
          {/* Indicador de Auto-Save */}
          <>
            {autoSaveStatus && (
              <div
                
                
                
                className={`mt-2 text-xs flex items-center justify-center gap-1 ${
                  autoSaveStatus === 'saving' ? 'text-white/70' :
                  autoSaveStatus === 'saved' ? 'text-green-200' :
                  'text-red-200'
                }`}
              >
                {autoSaveStatus === 'saving' && (
                  <>
                    <Loader2 className="w-3 h-3 animate-spin" />
                    Salvando...
                  </>
                )}
                {autoSaveStatus === 'saved' && (
                  <>
                    <Check className="w-3 h-3" />
                    Salvo automaticamente
                  </>
                )}
                {autoSaveStatus === 'error' && (
                  <>
                    <X className="w-3 h-3" />
                    Erro ao salvar
                  </>
                )}
              </div>
            )}
          </>
        </div>
      </div>

      {/* Form Content */}
      <main className="max-w-2xl mx-auto px-4 space-y-6">
        <>
          <div key={currentDay} className="space-y-6">
            {/* Data do dia */}
            <div className="bg-white rounded-3xl p-6 shadow-sm">
              <Label className="text-[#30244F] font-medium">Data deste registro</Label>
              <Input 
                type="date"
                value={currentDayData.date}
                onChange={(e) => updateDayField('date', e.target.value)}
                className="mt-2 border-[#FAE0D5] focus:border-[#8F7AC5]"
              />
            </div>

            {/* Sono Noturno */}
            <div className="bg-white rounded-3xl p-6 shadow-sm space-y-5">
              <h3 className="font-semibold text-[#30244F] flex items-center gap-2">
                <Moon className="w-5 h-5 text-[#8F7AC5]" />
                Sono Noturno
              </h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-[#30244F] text-sm">
                    Acordou de manhã às: <span className="text-red-500">*</span>
                  </Label>
                  <Input 
                    type="time"
                    value={currentDayData.wakeUpTime}
                    onChange={(e) => updateDayField('wakeUpTime', e.target.value)}
                    className={`mt-1 border-[#FAE0D5] focus:border-[#8F7AC5] ${!currentDayData.wakeUpTime ? 'border-red-300 bg-red-50' : ''}`}
                    required
                  />
                  {!currentDayData.wakeUpTime && (
                    <p className="text-xs text-red-500 mt-1">Campo obrigatório</p>
                  )}
                </div>
                <div>
                  <Label className="text-[#30244F] text-sm">
                    Horário que dormiu na noite anterior: <span className="text-red-500">*</span>
                  </Label>
                  <Input 
                    type="time"
                    value={currentDayData.bedTime}
                    onChange={(e) => updateDayField('bedTime', e.target.value)}
                    className={`mt-1 border-[#FAE0D5] focus:border-[#8F7AC5] ${!currentDayData.bedTime ? 'border-red-300 bg-red-50' : ''}`}
                    required
                  />
                  {!currentDayData.bedTime && (
                    <p className="text-xs text-red-500 mt-1">Campo obrigatório</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-[#30244F] text-sm">Quantas vezes acordou à noite?</Label>
                  <MobileSelect 
                    value={currentDayData.nightWakings} 
                    onValueChange={(v) => {
                      if (v === 'Outro (digitar)') {
                        updateDayField('nightWakings', '');
                        updateDayField('nightWakingsCustom', true);
                      } else {
                        updateDayField('nightWakings', v);
                        updateDayField('nightWakingsCustom', false);
                      }
                    }}
                    options={nightWakingsOptions}
                    placeholder="Selecione..."
                    className="mt-1"
                  />
                  {currentDayData.nightWakingsCustom && (
                    <Input 
                      placeholder="Digite aqui..."
                      value={currentDayData.nightWakings}
                      onChange={(e) => updateDayField('nightWakings', e.target.value)}
                      className="mt-2 border-[#FAE0D5] focus:border-[#8F7AC5]"
                    />
                  )}
                </div>
                <div>
                  <Label className="text-[#30244F] text-sm">Duração dos despertares</Label>
                  <MobileSelect 
                    value={currentDayData.nightWakingsDuration} 
                    onValueChange={(v) => {
                      if (v === 'Outro (digitar)') {
                        updateDayField('nightWakingsDuration', '');
                        updateDayField('nightWakingsDurationCustom', true);
                      } else {
                        updateDayField('nightWakingsDuration', v);
                        updateDayField('nightWakingsDurationCustom', false);
                      }
                    }}
                    options={nightWakingsDurationOptions}
                    placeholder="Selecione..."
                    className="mt-1"
                  />
                  {currentDayData.nightWakingsDurationCustom && (
                    <Input 
                      placeholder="Digite aqui..."
                      value={currentDayData.nightWakingsDuration}
                      onChange={(e) => updateDayField('nightWakingsDuration', e.target.value)}
                      className="mt-2 border-[#FAE0D5] focus:border-[#8F7AC5]"
                    />
                  )}
                </div>
              </div>
            </div>

            {/* Sonecas */}
            <div className="bg-white rounded-3xl p-6 shadow-sm space-y-5">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-[#30244F] flex items-center gap-2">
                  <Sun className="w-5 h-5 text-[#F2ACAA]" />
                  Sonecas do Dia
                </h3>
                {!currentDayData.noNaps && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={addNap}
                    className="text-[#8F7AC5] hover:bg-[#8F7AC5]/10"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Adicionar
                  </Button>
                )}
              </div>

              {/* Opção: Não fez soneca */}
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => {
                    updateDayField('noNaps', !currentDayData.noNaps);
                    if (!currentDayData.noNaps) {
                      updateDayField('naps', []);
                    } else {
                      updateDayField('naps', [{ startTime: '', duration: '', timeToSleep: '', howSlept: '', whereSlept: '' }]);
                    }
                  }}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm transition-all ${
                    currentDayData.noNaps
                      ? 'bg-[#F2ACAA] text-white'
                      : 'bg-[#FBF4FF] text-[#6B5B8C] hover:bg-[#F2ACAA]/20'
                  }`}
                >
                  {currentDayData.noNaps && <Check className="w-4 h-4" />}
                  Não fez soneca hoje
                </button>
              </div>

              {!currentDayData.noNaps && (currentDayData.naps || []).map((nap, napIndex) => (
                <div 
                  key={napIndex}
                  className="bg-[#FBF4FF] rounded-2xl p-4 space-y-4"
                  
                  
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-[#8F7AC5]">
                      Soneca {napIndex + 1}
                    </span>
                    {(currentDayData.naps || []).length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeNap(napIndex)}
                        className="text-red-400 hover:text-red-600 hover:bg-red-50 h-8 w-8 p-0"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                      <Label className="text-xs text-[#6B5B8C]">Horário</Label>
                      <Input 
                        type="time"
                        value={nap.startTime}
                        onChange={(e) => updateNap(napIndex, 'startTime', e.target.value)}
                        className="mt-1 text-sm border-[#D98DAE]/30 focus:border-[#8F7AC5]"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-[#6B5B8C]">Duração</Label>
                      <MobileSelect 
                        value={nap.duration} 
                        onValueChange={(v) => {
                          if (v === 'Outro (digitar)') {
                            updateNap(napIndex, 'duration', '');
                            updateNap(napIndex, 'durationCustom', true);
                          } else {
                            updateNap(napIndex, 'duration', v);
                            updateNap(napIndex, 'durationCustom', false);
                          }
                        }}
                        options={napDurationOptions}
                        placeholder="Selecione..."
                        className="mt-1 text-sm"
                      />
                      {nap.durationCustom && (
                        <Input 
                          placeholder="Ex: 35 min"
                          value={nap.duration}
                          onChange={(e) => updateNap(napIndex, 'duration', e.target.value)}
                          className="mt-1 text-sm border-[#D98DAE]/30 focus:border-[#8F7AC5]"
                        />
                      )}
                    </div>
                    <div>
                      <Label className="text-xs text-[#6B5B8C]">Tempo p/ dormir</Label>
                      <MobileSelect 
                        value={nap.timeToSleep} 
                        onValueChange={(v) => {
                          if (v === 'Outro (digitar)') {
                            updateNap(napIndex, 'timeToSleep', '');
                            updateNap(napIndex, 'timeToSleepCustom', true);
                          } else {
                            updateNap(napIndex, 'timeToSleep', v);
                            updateNap(napIndex, 'timeToSleepCustom', false);
                          }
                        }}
                        options={timeToSleepOptions}
                        placeholder="Selecione..."
                        className="mt-1 text-sm"
                      />
                      {nap.timeToSleepCustom && (
                        <Input 
                          placeholder="Ex: 25 min"
                          value={nap.timeToSleep}
                          onChange={(e) => updateNap(napIndex, 'timeToSleep', e.target.value)}
                          className="mt-1 text-sm border-[#D98DAE]/30 focus:border-[#8F7AC5]"
                        />
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-[#6B5B8C]">Como dormiu?</Label>
                      <MobileSelect 
                        value={nap.howSlept} 
                        onValueChange={(v) => updateNap(napIndex, 'howSlept', v)}
                        options={howBabySleepsOptions}
                        placeholder="Selecione..."
                        className="mt-1 text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-[#6B5B8C]">Onde dormiu?</Label>
                      <MobileSelect 
                        value={nap.whereSlept} 
                        onValueChange={(v) => updateNap(napIndex, 'whereSlept', v)}
                        options={whereBabySleepsOptions}
                        placeholder="Selecione..."
                        className="mt-1 text-sm"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Rotina do Dia - VERSÃO SIMPLIFICADA COM AUTOCOMPLETE */}
            <div className="bg-white rounded-3xl p-6 shadow-sm space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-[#30244F] flex items-center gap-2">
                  <Clock className="w-5 h-5 text-[#D98DAE]" />
                  Rotina do Dia
                </h3>
                {/* Botão visível apenas em desktop */}
                <Button
                  type="button"
                  size="sm"
                  onClick={addRoutineEntry}
                  className="hidden lg:flex bg-[#8F7AC5] hover:bg-[#7A68A8] text-white h-8 px-3"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Adicionar
                </Button>
              </div>

              <p className="text-xs text-[#6B5B8C]">
                Toque no <span className="lg:hidden inline-flex items-center justify-center w-5 h-5 bg-gradient-to-br from-[#8F7AC5] to-[#D98DAE] text-white rounded-full text-[10px]">+</span><span className="hidden lg:inline">botão Adicionar</span> para registrar uma atividade
              </p>

              {/* Entradas com autocomplete */}
              <div className="space-y-3">
                {(currentDayData.routineEntries || []).map((entry, entryIndex) => (
                  <div 
                    key={entryIndex}
                    className="bg-[#FBF4FF] rounded-xl p-3 space-y-2"
                    
                    
                  >
                    {/* Linha 1: Horário com botão Agora e lixeira */}
                    <div className="flex items-center justify-between">
                      <TimeSelector
                        value={entry.time}
                        onChange={(val) => updateRoutineEntry(entryIndex, 'time', val)}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeRoutineEntry(entryIndex)}
                        className="text-red-400 hover:text-red-600 hover:bg-red-50 h-8 w-8 p-0"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                    {/* Linha 2: Campo de atividade */}
                    <ActivityAutocomplete
                      value={entry.activity}
                      onChange={(val) => updateRoutineEntry(entryIndex, 'activity', val)}
                      placeholder="Digite a atividade..."
                    />
                  </div>
                ))}

                {/* Estado vazio */}
                {(currentDayData.routineEntries || []).length === 0 && (
                  <div className="text-center py-8 border-2 border-dashed border-[#FAE0D5] rounded-xl">
                    <Clock className="w-10 h-10 text-[#D98DAE] mx-auto mb-2" />
                    <p className="text-sm text-[#6B5B8C]">Nenhuma atividade registrada</p>
                    <p className="text-xs text-[#9890A8] mt-1">Use o botão <span className="inline-flex items-center justify-center w-6 h-6 bg-gradient-to-br from-[#8F7AC5] to-[#D98DAE] text-white rounded-full text-xs">+</span> para adicionar</p>
                    <p className="text-xs text-[#B8A9C9] mt-3 px-4">
                      Ex: Mamada, brincadeira, banho, passeio, alimentação, soneca...
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Observações */}
            <div className="bg-white rounded-3xl p-6 shadow-sm space-y-4">
              <div>
                <Label className="text-[#30244F] text-sm">Algo importante aconteceu hoje? (opcional)</Label>
                <Textarea 
                  placeholder="Vacina, viagem, visita, dentes nascendo, doença..."
                  value={currentDayData.importantNotes}
                  onChange={(e) => updateDayField('importantNotes', e.target.value)}
                  className="mt-1 min-h-[80px] border-[#FAE0D5] focus:border-[#8F7AC5]"
                />
              </div>
            </div>
          </div>
        </>
      </main>

      {/* Botão Flutuante de Adicionar Atividade - Mobile */}
      <button
        type="button"
        onClick={addRoutineEntry}
        className="lg:hidden fixed bottom-32 right-4 z-50 w-14 h-14 bg-gradient-to-br from-[#8F7AC5] to-[#D98DAE] text-white rounded-full shadow-lg flex items-center justify-center active:scale-95 transition-transform"
      >
        <Plus className="w-7 h-7" />
      </button>

      {/* Fixed Bottom Navigation */}
      <div className="fixed bottom-14 left-0 right-0 bg-white border-t border-[#FAE0D5] p-4 z-40">
        <div className="max-w-2xl mx-auto flex justify-between items-center">
          <Button
            variant="ghost"
            onClick={prevDay}
            className="text-[#6B5B8C]"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {currentDay === 1 ? 'Ver informações' : 'Dia anterior'}
          </Button>
          
          <div className="flex items-center gap-2">
            {Array.from({ length: totalDays }, (_, i) => (
              <div 
                key={i}
                className={`w-2 h-2 rounded-full transition-colors ${
                  i + 1 === currentDay ? 'bg-[#8F7AC5]' : i + 1 < currentDay ? 'bg-[#D98DAE]' : 'bg-[#FAE0D5]'
                }`}
              />
            ))}
          </div>

          <Button
            onClick={nextDay}
            disabled={saving}
            className="bg-[#8F7AC5] hover:bg-[#30244F] text-white px-6"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Salvando...
              </>
            ) : currentDay === totalDays ? (
              <>
                Finalizar
                <Sparkles className="w-4 h-4 ml-2" />
              </>
            ) : (
              <>
                Próximo dia
                <ArrowRight className="w-4 h-4 ml-2" />
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Modal - Visualizar Informações Iniciais */}
      <>
        {showInitialForm && (
          <div
            
            
            
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowInitialForm(false)}
          >
            <div
              
              
              
              className="bg-white rounded-3xl shadow-xl max-w-lg w-full max-h-[80vh] overflow-y-auto"
              onClick={e => e.stopPropagation()}
            >
              <div className="sticky top-0 bg-gradient-to-r from-[#8F7AC5] to-[#D98DAE] p-6 rounded-t-3xl">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <FileText className="w-6 h-6 text-white" />
                    <h2 className="text-xl font-bold text-white">Informações Iniciais</h2>
                  </div>
                  <button
                    onClick={() => setShowInitialForm(false)}
                    className="text-white/80 hover:text-white text-xl"
                  >
                    ✕
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-6">
                {/* Dados do Bebê */}
                {reportBasicInfo && (
                  <div className="space-y-4">
                    <h3 className="font-semibold text-[#30244F] flex items-center gap-2">
                      <span className="w-8 h-8 bg-[#8F7AC5]/10 rounded-full flex items-center justify-center">👶</span>
                      Dados do Bebê
                    </h3>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div className="bg-[#FBF4FF] p-3 rounded-xl">
                        <span className="text-[#6B5B8C] block text-xs">Nome</span>
                        <span className="text-[#30244F] font-medium">{reportBasicInfo.babyName || '-'}</span>
                      </div>
                      <div className="bg-[#FBF4FF] p-3 rounded-xl">
                        <span className="text-[#6B5B8C] block text-xs">Idade</span>
                        <span className="text-[#30244F] font-medium">{reportBasicInfo.babyAge ? `${reportBasicInfo.babyAge} meses` : '-'}</span>
                      </div>
                      <div className="bg-[#FBF4FF] p-3 rounded-xl">
                        <span className="text-[#6B5B8C] block text-xs">Mãe</span>
                        <span className="text-[#30244F] font-medium">{reportBasicInfo.motherName || '-'}</span>
                      </div>
                      <div className="bg-[#FBF4FF] p-3 rounded-xl">
                        <span className="text-[#6B5B8C] block text-xs">Pai</span>
                        <span className="text-[#30244F] font-medium">{reportBasicInfo.fatherName || '-'}</span>
                      </div>
                      <div className="bg-[#FBF4FF] p-3 rounded-xl col-span-2">
                        <span className="text-[#6B5B8C] block text-xs">Email</span>
                        <span className="text-[#30244F] font-medium">{reportBasicInfo.email || '-'}</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Formulário Inicial */}
                {initialFormData && (
                  <div className="space-y-4">
                    <h3 className="font-semibold text-[#30244F] flex items-center gap-2">
                      <span className="w-8 h-8 bg-[#D98DAE]/10 rounded-full flex items-center justify-center">📋</span>
                      Questionário do Sono
                    </h3>
                    
                    {initialFormData.sleepDifficulty && (
                      <div className="bg-[#FBF4FF] p-4 rounded-xl">
                        <span className="text-[#6B5B8C] block text-xs mb-1">Dificuldade com o sono</span>
                        <p className="text-[#30244F] text-sm">{initialFormData.sleepDifficulty}</p>
                      </div>
                    )}
                    
                    {initialFormData.expectations && (
                      <div className="bg-[#FBF4FF] p-4 rounded-xl">
                        <span className="text-[#6B5B8C] block text-xs mb-1">Expectativas</span>
                        <p className="text-[#30244F] text-sm">{initialFormData.expectations}</p>
                      </div>
                    )}

                    {initialFormData.howBabySleeps && (
                      <div className="bg-[#FBF4FF] p-4 rounded-xl">
                        <span className="text-[#6B5B8C] block text-xs mb-1">Como o bebê dorme</span>
                        <p className="text-[#30244F] text-sm">{initialFormData.howBabySleeps}</p>
                      </div>
                    )}

                    {initialFormData.sleepLocation && (
                      <div className="bg-[#FBF4FF] p-4 rounded-xl">
                        <span className="text-[#6B5B8C] block text-xs mb-1">Onde dorme</span>
                        <p className="text-[#30244F] text-sm">{initialFormData.sleepLocation}</p>
                      </div>
                    )}

                    {initialFormData.feedingType && (
                      <div className="bg-[#FBF4FF] p-4 rounded-xl">
                        <span className="text-[#6B5B8C] block text-xs mb-1">Alimentação</span>
                        <p className="text-[#30244F] text-sm">{initialFormData.feedingType}</p>
                      </div>
                    )}

                    {initialFormData.motherSleep && (
                      <div className="bg-[#FBF4FF] p-4 rounded-xl">
                        <span className="text-[#6B5B8C] block text-xs mb-1">Sono da mãe</span>
                        <p className="text-[#30244F] text-sm">{initialFormData.motherSleep}</p>
                      </div>
                    )}
                  </div>
                )}

                {!reportBasicInfo && !initialFormData && (
                  <div className="text-center py-8 text-[#9890A8]">
                    <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>Nenhuma informação inicial encontrada.</p>
                    <p className="text-sm mt-1">Preencha o questionário inicial primeiro.</p>
                  </div>
                )}

                {/* Botão para editar */}
                <div className="flex gap-3 pt-4 border-t border-[#FAE0D5]">
                  <Button
                    variant="outline"
                    onClick={() => setShowInitialForm(false)}
                    className="flex-1"
                  >
                    Fechar
                  </Button>
                  <Button
                    onClick={goToInitialForm}
                    className="flex-1 bg-[#8F7AC5] hover:bg-[#30244F] text-white"
                  >
                    Editar informações
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </>
    </div>
  );
};

export default RoutineRegistrationPage;
